import React from "react";
import type { InspectorInfo } from "../types";
import { fmtDateRange } from "../utils";
import { statusLabel } from "../../../utils/format";
import "./TaktInspector.css";

interface Props {
  info: InspectorInfo | null;
}

/**
 * Bottom inspector bar — floats over the board's bottom edge only while hovering.
 * Two columns: LEFT is the work-code group (swatch + label, one→many over
 * activities); RIGHT is the DISTINCT activity this bar is — its full name, the
 * prominent date range, then duration + float as labeled stats, with the status.
 * Fed imperatively from TaktGrid's hover bus, so the board never re-renders.
 */
const TaktInspector: React.FC<Props> = ({ info }) => {
  if (!info) return null;
  const st = statusLabel(info.status);
  const active = st === "Active";
  return (
    <div className="takt-inspector">
      {/* Column 1 — the work-code group (unchanged) */}
      <div className="takt-inspector__code">
        <span
          className="takt-inspector__swatch"
          style={{ background: info.fill, color: info.font }}
        >
          {info.workCode}
        </span>
        <span className="takt-inspector__code-name">{info.label || info.workCode}</span>
      </div>

      <div className="takt-inspector__divider" />

      {/* Column 2 — the distinct activity */}
      <div className="takt-inspector__activity">
        <div className="takt-inspector__name">
          {info.taskName || info.label || info.workCode}
        </div>
        <div className="takt-inspector__dates">{fmtDateRange(info.start, info.finish)}</div>
        <div className="takt-inspector__stats">
          <span className="takt-inspector__stat">
            <b>{info.remDur == null ? "—" : `${+info.remDur.toFixed(1)}d`}</b> Rem Duration
          </span>
          <span className="takt-inspector__stat-sep" />
          <span className="takt-inspector__stat">
            <b>{info.floatDays == null ? "—" : `${info.floatDays}d`}</b> float
          </span>
        </div>
      </div>

      <div className="takt-inspector__divider" />

      <div className="takt-inspector__trade">
        <div className="takt-inspector__trade-label">Trade</div>
        <div className="takt-inspector__trade-name">
          {info.tradeFill && (
            <span
              className="takt-inspector__trade-dot"
              style={{ background: info.tradeFill }}
            />
          )}
          <span className="takt-inspector__trade-text">{info.trade || "—"}</span>
        </div>
      </div>

      <span
        className={`takt-inspector__status${active ? " takt-inspector__status--active" : ""}`}
      >
        {st}
      </span>
    </div>
  );
};

export default TaktInspector;
